import { useId, useMemo, useState } from "react";
import { formatPhoneNumber } from "../lib/phone-format";

export type TechLineParticipant = { id: string; name: string; phone: string | null; active: boolean; mayCall: boolean; mayReceive: boolean };
type TeamMember = { id: string; active: boolean; isTechnician: boolean; technicianActive: boolean };
export type TechLineAccessProps = {
  team?: TeamMember[];
  participants: TechLineParticipant[];
  editable: boolean;
  onSave: (participant: TechLineParticipant, next: { mayCall: boolean; mayReceive: boolean }) => Promise<void>;
};

export function TechLineAccess({ participants: supplied, team, editable, onSave }: TechLineAccessProps) {
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const id = useId();
  // Tech Line callers can be any active company user; this panel deliberately
  // manages the narrower, explicitly selected recipient directory.
  const participants = useMemo(() => {
    if (!team) return supplied;
    const roster = new Map(team.map(member => [member.id, member]));
    return supplied.filter(person => roster.has(person.id)).map(person => {
      const member = roster.get(person.id)!;
      return { ...person, active: person.active && member.active && (!member.isTechnician || member.technicianActive) };
    });
  }, [supplied, team]);
  const activeCount = participants.filter(person => person.active).length;
  const query = search.trim().toLowerCase();
  const phoneQuery = query.replace(/\D/g, "");
  const visible = participants.filter(person => person.active === (tab === "active") && (
    !query || person.name.toLowerCase().includes(query) || Boolean(phoneQuery.length >= 3 && person.phone?.replace(/\D/g, "").includes(phoneQuery))
  ));
  const eligible = (person: TechLineParticipant) => person.active && Boolean(person.phone?.trim());
  const nextAccess = (receive: boolean) => ({ mayCall: true, mayReceive: receive });
  const selectTargets = visible.filter(person => eligible(person) && !person.mayReceive);
  const deselectTargets = visible.filter(person => eligible(person) && person.mayReceive);
  const selectTab = (next: "active" | "inactive") => { setTab(next); setSearch(""); setError(""); setNotice(""); };
  async function save(person: TechLineParticipant, receive: boolean) {
    if (!editable || busy || (receive && !eligible(person))) return;
    setBusy(person.id); setError(""); setNotice("");
    try { await onSave(person, nextAccess(receive)); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to update Tech Line recipients. Please try again."); }
    finally { setBusy(null); }
  }
  async function saveAll(receive: boolean) {
    if (!editable || busy) return;
    const targets = receive ? selectTargets : deselectTargets;
    if (!targets.length) return;
    setBusy("bulk"); setError(""); setNotice("");
    let cursor = 0, updated = 0;
    const failures: string[] = [];
    await Promise.all(Array.from({ length: Math.min(4, targets.length) }, async () => {
      while (cursor < targets.length) {
        const person = targets[cursor++];
        try { await onSave(person, nextAccess(receive)); updated++; }
        catch { failures.push(person.name); }
      }
    }));
    setBusy(null);
    if (failures.length) setError(`${updated} of ${targets.length} updated. Unable to update ${failures.slice(0, 3).join(", ")}${failures.length > 3 ? ` and ${failures.length - 3} others` : ""}. Try again for the remaining people.`);
    else setNotice(`${updated} ${updated === 1 ? "recipient" : "recipients"} updated.`);
  }
  return <>
    <div role="tablist" aria-label="Tech Line team status" className="mb-4 flex gap-1 border-b border-[var(--line)]" onKeyDown={event => {
      if (busy || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === "Home" ? "active" : event.key === "End" ? "inactive" : tab === "active" ? "inactive" : "active";
      selectTab(next);
      event.currentTarget.querySelector<HTMLButtonElement>(`[data-tab="${next}"]`)?.focus();
    }}>
      {(["active", "inactive"] as const).map(value => <button key={value} id={`${id}-${value}`} data-tab={value} type="button" role="tab" disabled={Boolean(busy)} aria-selected={tab === value} aria-controls={`${id}-members`} tabIndex={tab === value ? 0 : -1} onClick={() => selectTab(value)} className={`min-h-11 border-b-2 px-3 py-2 text-sm font-semibold ${tab === value ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-soft)]"}`}>{value === "active" ? `Active (${activeCount})` : `Inactive (${participants.length - activeCount})`}</button>)}
    </div>
    <div id={`${id}-members`} role="tabpanel" aria-labelledby={`${id}-${tab}`} aria-busy={Boolean(busy)}>
      <p className="mb-3 text-sm text-[var(--ink-soft)]">Active company users with a mobile number can call Tech Line. Select only the company members who should receive transferred calls.</p>
      <label className="mb-3 block text-sm font-medium">Search Tech Line recipients<input type="search" value={search} disabled={Boolean(busy)} onChange={event => setSearch(event.target.value)} placeholder="Name or phone number" className="mt-1 min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 font-normal" /></label>
      <div className="mb-2 flex flex-wrap gap-3">
        <button type="button" disabled={!editable || Boolean(busy) || !selectTargets.length} onClick={() => void saveAll(true)} className="min-h-11 rounded-lg border border-[var(--line)] px-3 text-sm font-semibold disabled:opacity-50">Select all</button>
        <button type="button" disabled={!editable || Boolean(busy) || !deselectTargets.length} onClick={() => void saveAll(false)} className="min-h-11 rounded-lg border border-[var(--line)] px-3 text-sm font-semibold disabled:opacity-50">Deselect all</button>
      </div>
      {error && <p role="alert" className="mb-3 text-sm text-[var(--st-conflict)]">{error}</p>}
      {notice && <p role="status" className="mb-3 text-sm text-[var(--ink-soft)]">{notice}</p>}
      {busy === "bulk" && <p role="status" className="mb-3 text-sm">Updating recipients…</p>}
      <div className="space-y-3">{visible.map(person => <div key={person.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--line)] pb-3">
        <div><p className="font-medium">{person.name}</p><p className="text-xs text-[var(--ink-faint)]">{formatPhoneNumber(person.phone) || "No mobile number"}</p></div>
        <label className="inline-flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" aria-label={`Tech Line recipient: ${person.name}`} checked={person.active && person.mayReceive} disabled={!editable || Boolean(busy) || !eligible(person)} onChange={event => void save(person, event.target.checked)} /> Receive Tech Line calls</label>
      </div>)}</div>
      {!visible.length && <p className="text-sm text-[var(--ink-soft)]">{query ? "No matching recipients in this tab." : tab === "active" ? "No active company members." : "No inactive company members."}</p>}
    </div>
  </>;
}
